import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mic, Play, Square, ChevronLeft, ArrowRight, CheckCircle2, Loader2, Sparkles, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { T, PrimaryBtn, HudFrame, AIPropsBackground, AuthBrand, useClickSound } from "@/pages/AuthPreviewShared";

/**
 * Free Assessment — short Speaking mock (3 quick questions, ~5 minutes).
 * For the PREVIEW we keep this lightweight:
 *  - simulated examiner prompts (no real STT yet — wired in a follow-up round to /api/ai/speaking/eval)
 *  - mic check + record toggle (uses MediaRecorder for realism)
 *  - on finish → mock result page with predicted band
 */
const QUESTIONS = [
  { id: 1, type: "warmup",  prompt: "Tell us your name and where you're from." },
  { id: 2, type: "topic",   prompt: "Describe a place you'd love to visit and why." },
  { id: 3, type: "opinion", prompt: "Do you think social media has made it easier to learn English? Why or why not?" },
];

export default function FreeAssessmentSpeaking() {
  const play = useClickSound();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [stage, setStage] = useState("brief"); // brief | q | finished
  const [qIdx, setQIdx] = useState(0);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const intervalRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("freeAssessmentLead");
    if (!raw) { nav("/free-assessment"); return; }
    try { setLead(JSON.parse(raw)); } catch (e) { nav("/free-assessment"); }
  }, [nav]);

  useEffect(() => () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      setElapsed(0);
      intervalRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone access is required for the speaking test.");
    }
  };

  const stop = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach((t) => t.stop());
    if (intervalRef.current) clearInterval(intervalRef.current);
    setRecording(false);
  };

  const next = () => {
    stop();
    if (qIdx + 1 < QUESTIONS.length) {
      setQIdx(qIdx + 1);
      setElapsed(0);
    } else {
      setStage("finished");
    }
  };

  const submitForReport = async () => {
    setSubmitting(true);
    // Simulated grading in preview — real eval will hit /api/ai/speaking/eval
    setTimeout(() => {
      setSubmitting(false);
      toast.success("Assessment complete — your predicted band is ready.");
      nav("/free-assessment/result");
    }, 1600);
  };

  const fmt = (s) => `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;

  if (!lead) return null;

  return (
    <div className="min-h-screen relative pb-20" style={{ background: T.bg, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="fa-speaking-root">
      <style>{`
        .display { font-family: 'Rajdhani', sans-serif; letter-spacing: -0.01em; }
        .mono    { font-family: 'IBM Plex Mono', monospace; }
      `}</style>
      <AIPropsBackground />

      <header className="relative max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <AuthBrand />
        <Link to="/free-assessment" onClick={() => play("hover")}
          className="inline-flex items-center gap-1 mono text-[10px] tracking-[0.3em] hover:underline"
          style={{ color: T.textMuted }}>
          <ChevronLeft className="w-3 h-3" /> EDIT DETAILS
        </Link>
      </header>

      <div className="relative max-w-3xl mx-auto px-6 lg:px-10 py-6">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {["Your details", "Speaking mock", "Predicted band"].map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center mono text-[10px] font-bold"
                style={{
                  background: i <= 1 ? T.primary : T.cardBg,
                  color: i <= 1 ? T.textOnPrimary : T.textMuted,
                  border: `1.5px solid ${i <= 1 ? T.primary : T.border}`,
                  boxShadow: i === 1 ? `0 0 12px ${T.primaryGlow}` : "none",
                }}>
                {i + 1}
              </div>
              <div className="mono text-[10px] tracking-[0.25em]" style={{ color: i <= 1 ? T.text : T.textMuted }}>{s.toUpperCase()}</div>
              {i < 2 && <div className="w-8 h-px mx-1" style={{ background: T.border }} />}
            </div>
          ))}
        </div>

        {stage === "brief" && (
          <HudFrame color={T.primary} className="p-1">
            <div className="rounded-sm p-8 lg:p-10 text-center" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
              <div className="mono text-[10px] tracking-[0.3em] mb-3 inline-flex items-center gap-1" style={{ color: T.primary }}>
                <Sparkles className="w-3 h-3" /> // HELLO, {lead.full_name.toUpperCase()}
              </div>
              <h2 className="display text-4xl md:text-5xl font-bold">Speaking Mock</h2>
              <p className="text-base mt-4 max-w-lg mx-auto" style={{ color: T.textMuted }}>
                3 short questions — total ~5 minutes. Speak naturally. Our AI examiner will score your fluency, vocabulary, grammar and pronunciation.
              </p>

              <div className="grid grid-cols-3 gap-3 mt-7 max-w-md mx-auto">
                {["3 Questions", "~5 Minutes", "Instant Band"].map((s, i) => (
                  <HudFrame key={i} className="p-3" color={T.primary}>
                    <div className="mono text-[10px] tracking-[0.25em] py-1" style={{ color: T.text }}>{s.toUpperCase()}</div>
                  </HudFrame>
                ))}
              </div>

              <div className="mt-8 mono text-[10px] tracking-[0.25em] inline-flex items-center gap-2 px-3 py-1.5 rounded-sm"
                style={{ color: T.textMuted, background: T.bg2 }}>
                <Volume2 className="w-3.5 h-3.5" />
                MICROPHONE ACCESS WILL BE REQUESTED · NO RECORDING IS STORED
              </div>

              <div className="mt-8">
                <PrimaryBtn onClick={() => setStage("q")} className="h-16 px-12 text-base" data-testid="fa-speaking-begin">
                  <Mic className="w-5 h-5" />
                  BEGIN SPEAKING MOCK
                  <ArrowRight className="w-5 h-5" />
                </PrimaryBtn>
              </div>
            </div>
          </HudFrame>
        )}

        {stage === "q" && (
          <HudFrame color={T.primary} className="p-1">
            <div className="rounded-sm p-7 lg:p-9" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
              <div className="flex items-center justify-between mb-5">
                <div className="mono text-[10px] tracking-[0.3em]" style={{ color: T.primary }}>// QUESTION {qIdx + 1} OF {QUESTIONS.length}</div>
                <div className="mono text-base tracking-[0.2em]" style={{ color: recording ? T.secondary : T.textMuted }}>
                  {recording ? `● REC ${fmt(elapsed)}` : "READY"}
                </div>
              </div>
              <h2 className="display text-3xl md:text-4xl font-bold leading-snug">{QUESTIONS[qIdx].prompt}</h2>
              <div className="mono text-[10px] tracking-[0.25em] mt-2" style={{ color: T.textMuted }}>
                {QUESTIONS[qIdx].type === "warmup" ? "WARM-UP" : QUESTIONS[qIdx].type.toUpperCase()}
              </div>

              <div className="mt-10 flex items-center justify-center gap-4">
                {!recording ? (
                  <button onClick={() => { play("primary"); start(); }} data-testid="fa-mic-start"
                    className="w-24 h-24 rounded-full inline-flex items-center justify-center transition-all duration-300 active:scale-95"
                    style={{
                      background: T.primary,
                      color: T.textOnPrimary,
                      boxShadow: `0 0 28px ${T.primaryGlow}, inset 0 0 16px rgba(255,255,255,0.25)`,
                    }}>
                    <Play className="w-9 h-9" />
                  </button>
                ) : (
                  <button onClick={() => { play("primary"); stop(); }} data-testid="fa-mic-stop"
                    className="w-24 h-24 rounded-full inline-flex items-center justify-center transition-all duration-300 active:scale-95 animate-pulse"
                    style={{
                      background: T.secondary,
                      color: "#fff",
                      boxShadow: `0 0 28px rgba(214,40,40,0.6)`,
                    }}>
                    <Square className="w-8 h-8" fill="#fff" />
                  </button>
                )}
              </div>
              <div className="mono text-[10px] tracking-[0.3em] text-center mt-3" style={{ color: T.textMuted }}>
                {recording ? "RECORDING · TAP STOP WHEN DONE" : "TAP TO START RECORDING YOUR ANSWER"}
              </div>

              <div className="mt-10 flex items-center justify-between">
                <button onClick={() => { play("hover"); setQIdx(Math.max(0, qIdx - 1)); }} disabled={qIdx === 0}
                  className="px-5 h-11 mono text-[10px] tracking-[0.25em] uppercase rounded-sm disabled:opacity-40"
                  style={{ color: T.textMuted, border: `1px solid ${T.border}` }}
                  data-testid="fa-prev-q">
                  ← Previous
                </button>
                <PrimaryBtn onClick={next} className="h-12 px-7" data-testid="fa-next-q">
                  {qIdx + 1 === QUESTIONS.length ? "FINISH MOCK" : "NEXT QUESTION"}
                  <ArrowRight className="w-4 h-4" />
                </PrimaryBtn>
              </div>
            </div>
          </HudFrame>
        )}

        {stage === "finished" && (
          <HudFrame color={T.primary} className="p-1">
            <div className="rounded-sm p-8 lg:p-10 text-center" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
              <div className="w-20 h-20 mx-auto rounded-sm flex items-center justify-center mb-4"
                style={{ background: `${T.success}15`, color: T.success, border: `1px solid ${T.success}40` }}>
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h2 className="display text-4xl font-bold">All 3 answers recorded.</h2>
              <p className="text-base mt-3 max-w-lg mx-auto" style={{ color: T.textMuted }}>
                Our AI examiner is scoring your responses on fluency, vocabulary, grammar and pronunciation. This takes a few seconds.
              </p>
              <div className="mt-8">
                <PrimaryBtn onClick={submitForReport} disabled={submitting} className="h-16 px-12 text-base" data-testid="fa-submit-report">
                  {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Scoring…</> : <>SEE MY PREDICTED BAND <ArrowRight className="w-5 h-5" /></>}
                </PrimaryBtn>
              </div>
            </div>
          </HudFrame>
        )}
      </div>
    </div>
  );
}
